<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <PrimeDialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    modal
    :header="
      editCriterion
        ? $t('rechercheAvancee.editCriterionPopupTitle')
        : $t('rechercheAvancee.addCriterionPopupTitle')
    "
    :style="{ width: '600px', maxWidth: '95vw' }"
  >
    <div class="flex flex-column gap-3">
      <div class="flex flex-column gap-2">
        <label class="font-semibold">{{
          $t("rechercheAvancee.propertyLabel")
        }}</label>
        <PrimeIconField>
          <PrimeInputIcon class="ri-search-line" />
          <PrimeInputText
            v-model="propertyFilter"
            :placeholder="$t('rechercheAvancee.searchProperty')"
            class="w-full"
            size="small"
          />
        </PrimeIconField>
        <PrimeDataTable
          v-model:selection="criterion.property"
          :value="filteredProperties"
          selection-mode="single"
          dataKey="id"
          :paginator="filteredProperties.length > 8"
          :rows="8"
          size="small"
          @row-select="onPropertyChange"
          scrollable
          scrollHeight="300px"
        >
          <PrimeColumn
            field="name"
            :header="$t('rechercheAvancee.propertyName')"
            sortable
          >
            <template #body="slotProps">
              <span class="font-semibold" v-if="slotProps.data.labelId">{{
                $t(slotProps.data.labelId)
              }}</span>
              <span class="font-semibold" v-else>{{
                slotProps.data.title
              }}</span>
            </template>
          </PrimeColumn>
          <PrimeColumn
            field="id"
            :header="$t('rechercheAvancee.propertyId')"
            sortable
          >
            <template #body="slotProps">
              <code class="text-sm">{{ slotProps.data.id }}</code>
            </template>
          </PrimeColumn>
        </PrimeDataTable>
      </div>

      <div class="flex gap-3">
        <div class="flex flex-column gap-1 flex-1">
          <label class="font-semibold">{{
            $t("rechercheAvancee.conditionLabel")
          }}</label>
          <PrimeSelectButton
            v-model="criterion.negation"
            :options="negationOptions"
            optionLabel="label"
            optionValue="value"
          />
        </div>
        <div class="flex flex-column gap-1 flex-1">
          <label class="font-semibold">{{
            $t("rechercheAvancee.operatorLabel")
          }}</label>
          <PrimeSelect
            v-model="criterion.operator"
            :options="getOperatorsForCriterion()"
            :optionLabel="(option) => $t(option.labelKey)"
            optionValue="value"
            :placeholder="$t('rechercheAvancee.operatorPlaceholder')"
            :disabled="!criterion.property"
          />
        </div>
      </div>

      <div class="flex flex-column gap-1">
        <label class="font-semibold">{{
          $t("rechercheAvancee.valueLabel")
        }}</label>
        <div class="flex gap-2 align-items-center">
          <PrimeInputText
            v-if="propertyType === 'text'"
            v-model="criterion.value"
            class="w-full"
          />
          <PrimeSelectButton
            v-if="propertyType === 'boolean'"
            v-model="criterion.value"
            :options="booleanOptions"
            optionLabel="label"
            optionValue="value"
          />
          <PrimeDatePicker
            v-if="propertyType === 'date' || propertyType === 'datetime'"
            v-model="criterion.value"
            dateFormat="yy-mm-dd"
            class="w-full"
          />
          <PrimeDatePicker
            v-if="
              (propertyType === 'date' || propertyType === 'datetime') &&
              criterion.operator === 'range'
            "
            v-model="criterion.value_end"
            dateFormat="yy-mm-dd"
            class="w-full"
          />
          <PrimeInputNumber
            v-if="['int', 'double', 'number'].includes(propertyType)"
            v-model="criterion.value"
            class="w-full"
          />
          <PrimeInputNumber
            v-if="
              ['int', 'double', 'number'].includes(propertyType) &&
              criterion.operator === 'range'
            "
            v-model="criterion.value_end"
            class="w-full"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <PrimeButton
        :label="$t('rechercheAvancee.cancelButton')"
        icon="ri-close-line"
        @click="$emit('update:visible', false)"
        text
      />
      <PrimeButton
        v-if="editCriterion"
        :label="$t('rechercheAvancee.updateButton')"
        icon="ri-check-line"
        @click="updateCriterion"
        :disabled="!isCriterionValid"
      />
      <PrimeButton
        v-else
        :label="$t('rechercheAvancee.addButton')"
        icon="ri-add-line"
        @click="addCriterion"
        :disabled="!isCriterionValid"
      />
    </template>
  </PrimeDialog>
</template>

<script>
import Dialog from "primevue/dialog";
import Select from "primevue/select";
import SelectButton from "primevue/selectbutton";
import InputText from "primevue/inputtext";
import DatePicker from "primevue/datepicker";
import InputNumber from "primevue/inputnumber";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";

const DEFAULT_CRITERION = {
  id: null,
  property: null,
  negation: false,
  operator: null,
  value: null,
  value_end: null,
};

export default {
  name: "AddCriterionPopup",
  components: {
    PrimeDialog: Dialog,
    PrimeSelect: Select,
    PrimeSelectButton: SelectButton,
    PrimeInputText: InputText,
    PrimeDatePicker: DatePicker,
    PrimeInputNumber: InputNumber,
    PrimeButton: Button,
    PrimeDataTable: DataTable,
    PrimeColumn: Column,
    PrimeIconField: IconField,
    PrimeInputIcon: InputIcon,
  },
  props: {
    visible: Boolean,
    properties: {
      type: Array,
      required: true,
    },
    editCriterion: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:visible", "add-criterion", "update-criterion"],
  watch: {
    visible(newVal) {
      if (newVal && this.editCriterion) {
        // Deep clone the criterion for editing
        const clone = JSON.parse(JSON.stringify(this.editCriterion));
        // Re-parse dates for PrimeDatePicker
        if (
          clone.property &&
          (clone.property.input === "date" ||
            clone.property.input === "datetime")
        ) {
          if (clone.value) {
            clone.value = new Date(clone.value);
          }
          if (clone.value_end) {
            clone.value_end = new Date(clone.value_end);
          }
        }
        // Find the property reference in the properties array (for DataTable selection)
        if (clone.property && clone.property.id) {
          const found = this.properties.find((p) => p.id === clone.property.id);
          if (found) {
            clone.property = found;
          }
        }
        this.criterion = clone;
      } else if (newVal) {
        this.criterion = { ...DEFAULT_CRITERION };
      }
      if (newVal) {
        this.propertyFilter = "";
      }
    },
  },
  data() {
    return {
      criterion: { ...DEFAULT_CRITERION },
      propertyFilter: "",
      operatorOptions: {
        text: [
          { labelKey: "rechercheAvancee.exact", value: "exact" },
          { labelKey: "rechercheAvancee.contains", value: "contains" },
        ],
        date: [
          { labelKey: "rechercheAvancee.isDate", value: "exact" },
          { labelKey: "rechercheAvancee.isDateRange", value: "range" },
        ],
        datetime: [
          { labelKey: "rechercheAvancee.isDate", value: "exact" },
          { labelKey: "rechercheAvancee.isDateRange", value: "range" },
        ],
        number: [
          { labelKey: "rechercheAvancee.isNumber", value: "exact" },
          { labelKey: "rechercheAvancee.isNumberRange", value: "range" },
        ],
        int: [
          { labelKey: "rechercheAvancee.isNumber", value: "exact" },
          { labelKey: "rechercheAvancee.isNumberRange", value: "range" },
        ],
        double: [
          { labelKey: "rechercheAvancee.isNumber", value: "exact" },
          { labelKey: "rechercheAvancee.isNumberRange", value: "range" },
        ],
        boolean: [{ labelKey: "rechercheAvancee.is", value: "exact" }],
      },
    };
  },
  computed: {
    negationOptions() {
      return [
        {
          label: `${this.$t("rechercheAvancee.mustContain")} (+)`,
          value: false,
        },
        {
          label: `${this.$t("rechercheAvancee.mustNotContain")} (-)`,
          value: true,
        },
      ];
    },
    booleanOptions() {
      return [
        { label: this.$t("true"), value: true },
        { label: this.$t("false"), value: false },
      ];
    },
    filteredProperties() {
      const query = this.propertyFilter?.toLowerCase().trim();
      if (!query) {
        return this.properties;
      }
      return this.properties.filter((p) => {
        const label = p.labelId ? this.$t(p.labelId) : p.title || "";
        return (
          label.toLowerCase().includes(query) ||
          p.id.toLowerCase().includes(query)
        );
      });
    },
    isCriterionValid() {
      return (
        this.criterion.property &&
        this.criterion.operator &&
        this.criterion.value !== null &&
        this.criterion.value !== ""
      );
    },
    propertyType() {
      return this.criterion.property ? this.criterion.property.input : "text";
    },
  },
  methods: {
    getOperatorsForCriterion() {
      const type = this.propertyType;
      return this.operatorOptions[type] || [];
    },
    onPropertyChange() {
      this.criterion.operator = null;
      this.criterion.value = null;
      this.criterion.value_end = null;
    },
    addCriterion() {
      const operatorOption = this.getOperatorsForCriterion().find(
        (op) => op.value === this.criterion.operator,
      );
      this.$emit("add-criterion", {
        ...this.criterion,
        id: Date.now(),
        operatorLabelKey: operatorOption
          ? operatorOption.labelKey
          : this.criterion.operator,
      });
      this.criterion = { ...DEFAULT_CRITERION };
      this.$emit("update:visible", false);
    },
    updateCriterion() {
      const operatorOption = this.getOperatorsForCriterion().find(
        (op) => op.value === this.criterion.operator,
      );
      this.$emit("update-criterion", {
        ...this.criterion,
        operatorLabelKey: operatorOption
          ? operatorOption.labelKey
          : this.criterion.operator,
      });
      this.criterion = { ...DEFAULT_CRITERION };
      this.$emit("update:visible", false);
    },
  },
};
</script>
